/**
 * 响应式 Storage 配置
 * @description 封装 responsive-storage 的初始化配置
 */

import type { App } from 'vue';
import Storage from 'responsive-storage';

/**
 * 服务器配置接口
 */
export interface ServerConfigs {
  Locale?: string;
  Layout?: string;
  Theme?: string;
  DarkMode?: boolean;
  SidebarStatus?: boolean;
  EpThemeColor?: string;
  Grey?: boolean;
  Weak?: boolean;
  HideTabs?: boolean;
  ShowLogo?: boolean;
  ShowModel?: string;
  MultiTagsCache?: boolean;
}

/**
 * 响应式存储配置选项
 */
export interface ResponsiveStorageOptions {
  /** 命名空间，默认为 "responsive-" */
  nameSpace?: string;
  /** 默认路由数组（用于 tags） */
  defaultRouterArrays?: any[];
  /** 服务器配置 */
  config: ServerConfigs;
}

/**
 * 获取浏览器语言
 * @returns 语言代码 "zh" 或 "en"
 */
export function getBrowserLanguage(): string {
  const nav = navigator as any;
  const browserLang = (nav.language || nav.browserLanguage).toLowerCase();
  return browserLang.indexOf('zh') > -1 ? 'zh-CN' : 'en-US';
}

/**
 * 注入响应式存储到 Vue 应用
 * @param app - Vue 应用实例
 * @param options - 配置选项
 */
export function injectResponsiveStorage(app: App, options: ResponsiveStorageOptions): void {
  const { nameSpace = 'responsive-', defaultRouterArrays = [], config } = options;

  const language = getBrowserLanguage();

  const configObj = Object.assign(
    {
      // 国际化 默认中文zh
      locale: Storage.getData('locale', nameSpace) ?? {
        locale: language ?? config.Locale ?? 'zh-CN',
      },
      // layout模式以及主题
      layout: Storage.getData('layout', nameSpace) ?? {
        layout: config.Layout ?? 'vertical',
        theme: config.Theme ?? 'default',
        darkMode: config.DarkMode ?? false,
        sidebarStatus: config.SidebarStatus ?? true,
        epThemeColor: config.EpThemeColor ?? '#409EFF',
      },
      configure: Storage.getData('configure', nameSpace) ?? {
        grey: config.Grey ?? false,
        weak: config.Weak ?? false,
        hideTabs: config.HideTabs ?? false,
        showLogo: config.ShowLogo ?? true,
        showModel: config.ShowModel ?? 'smart',
        multiTagsCache: config.MultiTagsCache ?? false,
      },
    },
    config.MultiTagsCache
      ? {
          // 默认显示首页tag
          tags: Storage.getData('tags', nameSpace) ?? defaultRouterArrays,
        }
      : {},
  );

  app.use(Storage, { nameSpace, memory: configObj });
}

/**
 * 创建响应式存储注入函数（兼容旧的使用方式）
 * @param defaultRouterArrays - 默认路由数组
 * @returns 注入函数
 */
export function createResponsiveStorageInjector(defaultRouterArrays: any[] = []) {
  return (app: App, config: ServerConfigs) => {
    injectResponsiveStorage(app, {
      config,
      defaultRouterArrays,
    });
  };
}
